import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Phone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { PHONE_DISPLAY, PHONE_TEL, SERVICE_AREAS, SERVICE_TYPES } from "@/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", service_type: "", area: "", message: "" };

const FIELD =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-gold focus:ring-2 focus:ring-gold/30";

export const QuoteDialog = ({ open, onOpenChange, service }) => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open && service) setForm((f) => ({ ...f, service_type: service }));
    }, [open, service]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/quotes`, form);
            toast.success("Request received", {
                description: "Thanks — we'll get back to you shortly. For urgent situations, call us now.",
            });
            onOpenChange(false);
            setForm(EMPTY);
        } catch (err) {
            toast.error("Could not send your request", {
                description: `Please try again, or call us directly at ${PHONE_DISPLAY}.`,
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="quote-modal" className="rounded-2xl sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-semibold text-slate-900">
                        Get a Free Quote
                    </DialogTitle>
                    <DialogDescription className="text-sm text-slate-600">
                        Tell us what you need and we'll get back to you. Locked out right now?{" "}
                        <a data-testid="quote-modal-call-link" href={PHONE_TEL} className="font-bold text-gold-dark hover:text-gold">
                            Call {PHONE_DISPLAY}
                        </a>
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="mt-2 space-y-4">
                    <div>
                        <label htmlFor="quote-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                            Name *
                        </label>
                        <input
                            id="quote-name"
                            data-testid="quote-name-input"
                            required
                            minLength={2}
                            value={form.name}
                            onChange={set("name")}
                            placeholder="Your name"
                            className={FIELD}
                        />
                    </div>
                    <div>
                        <label htmlFor="quote-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                            Phone *
                        </label>
                        <input
                            id="quote-phone"
                            data-testid="quote-phone-input"
                            required
                            minLength={7}
                            type="tel"
                            value={form.phone}
                            onChange={set("phone")}
                            placeholder="306-555-0123"
                            className={FIELD}
                        />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label htmlFor="quote-service" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                                Service *
                            </label>
                            <select
                                id="quote-service"
                                data-testid="quote-service-select"
                                required
                                value={form.service_type}
                                onChange={set("service_type")}
                                className={FIELD}
                            >
                                <option value="" disabled>
                                    Select service
                                </option>
                                {SERVICE_TYPES.map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="quote-area" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                                Area
                            </label>
                            <select
                                id="quote-area"
                                data-testid="quote-area-select"
                                value={form.area}
                                onChange={set("area")}
                                className={FIELD}
                            >
                                <option value="">Select area</option>
                                {SERVICE_AREAS.map((a) => (
                                    <option key={a} value={a}>
                                        {a}
                                    </option>
                                ))}
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="quote-message" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
                            Details
                        </label>
                        <textarea
                            id="quote-message"
                            data-testid="quote-message-input"
                            rows={3}
                            value={form.message}
                            onChange={set("message")}
                            placeholder="e.g. Locked out of my house, deadbolt needs replacing, lost my car key…"
                            className={FIELD}
                        />
                    </div>
                    <button
                        data-testid="quote-submit-button"
                        type="submit"
                        disabled={sending}
                        className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-gold px-7 py-4 text-sm font-extrabold uppercase tracking-wider text-navy-950 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60"
                    >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                        {sending ? "Sending…" : "Request My Free Quote"}
                    </button>
                </form>
            </DialogContent>
        </Dialog>
    );
};
